import { supabase } from '../lib/supabase';
import {
  PricingScenario,
  ProfitMarginAnalysis,
  BreakevenAnalysis,
  RecurringTransaction,
  CashFlowProjection,
  CashFlowProjectionItem,
  PricingStrategy
} from '../types/finance';

// Price Calculator Service
export const pricingScenarioService = {
  async getAll() {
    const { data, error } = await supabase
      .from('pricing_scenarios')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as PricingScenario[];
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('pricing_scenarios')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as PricingScenario;
  },

  async create(scenario: PricingScenario) {
    const { data, error } = await supabase
      .from('pricing_scenarios')
      .insert([scenario])
      .select();

    if (error) throw error;
    return data[0] as PricingScenario;
  },

  async update(id: string, scenario: Partial<PricingScenario>) {
    const { data, error } = await supabase
      .from('pricing_scenarios')
      .update(scenario)
      .eq('id', id)
      .select();

    if (error) throw error;
    return data[0] as PricingScenario;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('pricing_scenarios')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  }
};

// Profit Margin Analyzer Service
export const profitMarginService = {
  async getAll() {
    const { data, error } = await supabase
      .from('profit_margin_analyses')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as ProfitMarginAnalysis[];
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('profit_margin_analyses')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ProfitMarginAnalysis;
  },

  async create(analysis: ProfitMarginAnalysis) {
    const { data, error } = await supabase
      .from('profit_margin_analyses')
      .insert([analysis])
      .select();

    if (error) throw error;
    return data[0] as ProfitMarginAnalysis;
  },

  async update(id: string, analysis: Partial<ProfitMarginAnalysis>) {
    const { data, error } = await supabase
      .from('profit_margin_analyses')
      .update(analysis)
      .eq('id', id)
      .select();

    if (error) throw error;
    return data[0] as ProfitMarginAnalysis;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('profit_margin_analyses')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  }
};

// Break-Even Calculator Service
export const breakevenService = {
  async getAll() {
    const { data, error } = await supabase
      .from('breakeven_analyses')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as BreakevenAnalysis[];
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('breakeven_analyses')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as BreakevenAnalysis;
  },

  async create(analysis: BreakevenAnalysis) {
    const { data, error } = await supabase
      .from('breakeven_analyses')
      .insert([analysis])
      .select();

    if (error) throw error;
    return data[0] as BreakevenAnalysis;
  },

  async update(id: string, analysis: Partial<BreakevenAnalysis>) {
    const { data, error } = await supabase
      .from('breakeven_analyses')
      .update(analysis)
      .eq('id', id)
      .select();

    if (error) throw error;
    return data[0] as BreakevenAnalysis;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('breakeven_analyses')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  }
};

// Recurring Transaction Service
export const recurringTransactionService = {
  async getAll() {
    const { data, error } = await supabase
      .from('recurring_transactions')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as RecurringTransaction[];
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('recurring_transactions')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as RecurringTransaction;
  },

  async create(transaction: RecurringTransaction) {
    const { data, error } = await supabase
      .from('recurring_transactions')
      .insert([transaction])
      .select();

    if (error) throw error;
    return data[0] as RecurringTransaction;
  },

  async update(id: string, transaction: Partial<RecurringTransaction>) {
    const { data, error } = await supabase
      .from('recurring_transactions')
      .update(transaction)
      .eq('id', id)
      .select();

    if (error) throw error;
    return data[0] as RecurringTransaction;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('recurring_transactions')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  }
};

// Cash Flow Projection Service
export const cashFlowService = {
  async getAll() {
    const { data, error } = await supabase
      .from('cash_flow_projections')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as CashFlowProjection[];
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('cash_flow_projections')
      .select(`
        *,
        items:cash_flow_projection_items(*)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as CashFlowProjection;
  },

  async create(projection: CashFlowProjection, items: CashFlowProjectionItem[]) {
    // Create the projection
    const { data: projectionData, error: projectionError } = await supabase
      .from('cash_flow_projections')
      .insert([{
        user_id: projection.user_id,
        name: projection.name,
        start_date: projection.start_date,
        end_date: projection.end_date,
        initial_balance: projection.initial_balance,
        final_balance: projection.final_balance
      }])
      .select();

    if (projectionError) throw projectionError;
    
    // Add the projection ID to each item
    const itemsWithProjectionId = items.map(item => ({
      ...item,
      projection_id: projectionData[0].id
    }));

    // Insert the projection items
    const { error: itemsError } = await supabase
      .from('cash_flow_projection_items')
      .insert(itemsWithProjectionId);

    if (itemsError) throw itemsError;

    return this.getById(projectionData[0].id);
  },

  async update(id: string, projection: Partial<CashFlowProjection>, items?: CashFlowProjectionItem[]) {
    // Update the projection
    const { error: projectionError } = await supabase
      .from('cash_flow_projections')
      .update(projection)
      .eq('id', id);

    if (projectionError) throw projectionError;

    // If items are provided, delete existing items and insert new ones
    if (items) {
      // Delete existing items
      const { error: deleteError } = await supabase
        .from('cash_flow_projection_items')
        .delete()
        .eq('projection_id', id);

      if (deleteError) throw deleteError;

      // Insert new items
      const itemsWithProjectionId = items.map(item => ({
        ...item,
        projection_id: id
      }));

      const { error: itemsError } = await supabase
        .from('cash_flow_projection_items')
        .insert(itemsWithProjectionId);

      if (itemsError) throw itemsError;
    }

    return this.getById(id);
  },

  async delete(id: string) {
    // Delete projection items first (cascade should handle this, but being explicit)
    const { error: itemsError } = await supabase
      .from('cash_flow_projection_items')
      .delete()
      .eq('projection_id', id);

    if (itemsError) throw itemsError;

    // Delete the projection
    const { error } = await supabase
      .from('cash_flow_projections')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  }
};

// Pricing Strategy Service
export const pricingStrategyService = {
  async getAll() {
    const { data, error } = await supabase
      .from('pricing_strategies')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as PricingStrategy[];
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('pricing_strategies')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as PricingStrategy;
  },

  async create(strategy: PricingStrategy) {
    const { data, error } = await supabase
      .from('pricing_strategies')
      .insert([strategy])
      .select();

    if (error) throw error;
    return data[0] as PricingStrategy;
  },

  async update(id: string, strategy: Partial<PricingStrategy>) {
    const { data, error } = await supabase
      .from('pricing_strategies')
      .update(strategy)
      .eq('id', id)
      .select();

    if (error) throw error;
    return data[0] as PricingStrategy;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('pricing_strategies')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  }
};
